import * as helpers from './helpers';
import { SubkindConverterMap, withValueWrapping } from './shared';
import { DocumentCriterion, SearchOperator as Op } from '@fullstory/client-api';
import { FieldScope, VarType } from '@fullstory/search-vars';
import { trimPrefix, trimSuffix } from '@fullstory/util/string';
import * as segment from '@fullstory/proto/fs/api/segment';
import {
  Kind,
  SearchType,
  UiOnlySubkinds,
  isCustomStringType,
  isCustomDateType,
  isCustomNumberType,
  isCustomBoolType,
  CustomFieldType,
} from '@fullstory/search-types';
import { newConverterMap, partiallyEqualCriteria } from './helpers';
import { assertExhaustive } from '@fullstory/typeutils';

function cleanString(value: string, prefix: string, suffix: string): string {
  return trimSuffix(trimPrefix(value, `${prefix}_`), `_${suffix}`);
}

/**
 * Returns a Converter Spec Map for a particular specified scope. The returned map
 * reflects the operator map where for each proto based operator (which is also the search type subkind)
 * there exists a converter spec for that. These helper functions are shared across the custom fields
 * implementations, since the primary difference is in the scope values, e.g.:
 *
 *    >> evt_fieldname_str
 *    >> user_fieldname_str
 *    >> user_fieldname_strs
 *
 * and parsing them from search criteria is a matter of checking whether the first bits are
 * evt or user (or other scopes not yet implemented).
 *
 * Since the mapping is from proto operator -> search operator, and search types are the same as
 * their proto representations, a proto user filter can key directly into the map to get the
 * converter spec rather than iterating over it and checking to see if the produced criteria
 * matches. E.g given a proto segment definition:
 *
 *   userVarString: {
 *     startsWith: {
 *       value: ["test"],
 *       name: "string test",
 *     }
 *   }
 *
 *  startsWith is used to key into the map and fetch the appropriate converter spec.
 *
 * @param kind The top level search type kind
 * @param scope The var scope, e.g. evt or user or other
 * @param suffix oneof str or strs
 */
export function customVarString<T extends SearchType, K extends Kind<T>>(
  kind: K,
  scope: FieldScope,
  suffix: VarType.Str | VarType.Strs,
): SubkindConverterMap<T, K> {
  const result = newConverterMap<T, K>();
  const operators: Map<string, Op> = new Map([
    ['equals', Op.SEARCH_OP_EQ],
    ['notEquals', Op.SEARCH_OP_NEQ],
    ['startsWith', Op.SEARCH_OP_SW],
    ['endsWith', Op.SEARCH_OP_EW],
    ['substring', Op.SEARCH_OP_SUBSTR],
  ]);

  // partialExtractIdx is meant for extracting partial custom events and their dependent properties.
  // The primary use case is omnibox where omnisuggest will return criteria like:
  //   { Field: 'EventType', Op: Op.SEARCH_OP_EQ, Value: 'custom' },
  //   { Field: 'EventCustomName', Op: Op.SEARCH_OP_EQ, Value: 'Created Segment' },
  //   { Field: 'evt_signedUp_date', Op: Op.SEARCH_OP_NOT_RNGCC, Value: ['2019-02-04T18:30:00.000Z', '2019-02-14T18:30:00.000Z'] },
  //
  // and in order to extract the custom field, you need to key into the 3rd entry item.
  const partialExtractIdx = scope == FieldScope.Event ? 2 : 0;
  for (const [protoOperator, searchOperator] of operators.entries()) {
    result[protoOperator] = {
      criteria: s => {
        if (isCustomStringType(s)) {
          switch (s.field.type) {
            case CustomFieldType.Name:
              // only one we need to support for legacy criteria
              return [
                { Field: `${scope}_${s.field.name}_${suffix}`, Op: searchOperator, Value: s.value },
              ];
          }
        }
        return [];
      },
      withoutPartials: s => helpers.stripPartials(s, helpers.multiNotNullPartial),
      extractFromCriteria: (c: DocumentCriterion[]) => {
        if (c[0] == null) {
          return null;
        }
        return {
          kind,
          subkind: protoOperator,
          field: {
            type: CustomFieldType.Name,
            name: cleanString(c[0].Field, scope, suffix),
          },
          value: c[0].Value as string[],
        };
      },
      extractFromPartialCriteria: (c: DocumentCriterion[]) => {
        if (scope == FieldScope.User && protoOperator != 'equals') {
          // NB(milan): Maintaining continuity to only have the equals be supported for partials
          // i.e. omnibox
          return null;
        }
        if (!c || c.length < partialExtractIdx + 1 || c[partialExtractIdx] == null) {
          return null;
        }
        const toRet = {
          kind,
          subkind: protoOperator,
          field: {
            type: CustomFieldType.Name,
            name: cleanString(c[partialExtractIdx].Field, scope, suffix),
          },
          value: [null],
        };

        if (partiallyEqualCriteria(result[protoOperator].criteria(toRet), [c[partialExtractIdx]])) {
          return toRet;
        }

        return null;
      },
      searchTypeToFilter: st => helpers.searchTypeToFilterWithField(st),
      filterToSearchType: sf => helpers.filterToSearchTypeWithField(sf),
    };
  }

  for (const k of Object.keys(result)) {
    result[k] = withValueWrapping(result[k], 0);
  }

  for (const k of ['isSet', 'isUnset']) {
    result[k] = {
      withoutPartials: s => helpers.stripPartials(s, helpers.identity),
      searchTypeToFilter: st => helpers.searchTypeToFilterWithFieldIsSet(st),
      filterToSearchType: sf => helpers.filterToSearchTypeWithFieldIsSet(sf),
    };
  }

  return result;
}

/**
 * Returns a Converter Spec Map for a particular specified scope. The returned map
 * reflects the operator map where for each proto based operator (which is also the search type subkind)
 * there exists a converter spec for that. These helper functions are shared across the custom fields
 * implementations, since the primary difference is in the scope values, e.g.:
 *
 *    >> evt_fieldname_int
 *    >> user_fieldname_int
 *    >> user_fieldname_reals
 *
 * and parsing them from search criteria is a matter of checking whether the first bits are
 * evt or user (or other scopes not yet implemented).
 *
 * Since the mapping is from proto operator -> search operator, and search types are the same as
 * their proto representations, a proto user filter can key directly into the map to get the
 * converter spec rather than iterating over it and checking to see if the produced criteria
 * matches. E.g given a proto segment definition:
 *
 *   userVarInt: {
 *     equals: {
 *       value: 10,
 *       name: "numbers test",
 *     }
 *   }
 *
 *  equals is used to key into the map and fetch the appropriate converter spec.
 *
 * @param kind The top level search type kind
 * @param scope The var scope, e.g. evt or user or other
 * @param suffix oneof int, ints, real or reals
 */
export function customVarNumbers<T extends SearchType, K extends Kind<T>>(
  kind: K,
  scope: FieldScope,
  suffix: VarType.Int | VarType.Real | VarType.Ints | VarType.Reals,
): SubkindConverterMap<T, K> {
  const { searchTypeToFilterModifier, filterToSearchTypeModifier } =
    helpers.getNumericModifyFuncs(suffix);

  const result = newConverterMap<T, K>();
  const operators: Map<string, Op> = new Map([
    ['equals', Op.SEARCH_OP_EQ],
    ['notEquals', Op.SEARCH_OP_NEQ],
    ['lessThan', Op.SEARCH_OP_LT],
    ['greaterThan', Op.SEARCH_OP_GT],
    ['atMost', Op.SEARCH_OP_LTE],
    ['atLeast', Op.SEARCH_OP_GTE],
  ]);

  // partialExtractIdx is meant for extracting partial custom events and their dependent properties.
  // The primary use case is omnibox where omnisuggest will return criteria like:
  //   { Field: 'EventType', Op: Op.SEARCH_OP_EQ, Value: 'custom' },
  //   { Field: 'EventCustomName', Op: Op.SEARCH_OP_EQ, Value: 'Created Segment' },
  //   { Field: 'evt_signedUp_date', Op: Op.SEARCH_OP_NOT_RNGCC, Value: ['2019-02-04T18:30:00.000Z', '2019-02-14T18:30:00.000Z'] },
  //
  // and in order to extract the custom field, you need to key into the 3rd entry item.
  const partialExtractIdx = scope == FieldScope.Event ? 2 : 0;
  for (const [protoOperator, searchOperator] of operators.entries()) {
    result[protoOperator] = {
      criteria: s => {
        if (isCustomNumberType(s)) {
          switch (s.field.type) {
            case CustomFieldType.Name:
              // only one we need to support for legacy criteria
              return [
                { Field: `${scope}_${s.field.name}_${suffix}`, Op: searchOperator, Value: s.value },
              ];
          }
        }
        return [];
      },
      withoutPartials: s => helpers.stripPartials(s, helpers.notNullPartial),
      extractFromCriteria: (c: DocumentCriterion[]) => {
        if (c[0] == null) {
          return null;
        }
        return {
          kind,
          subkind: protoOperator,
          field: {
            type: CustomFieldType.Name,
            name: cleanString(c[0].Field, scope, suffix),
          },
          value: c[0].Value as number[],
        };
      },
      extractFromPartialCriteria: (c: DocumentCriterion[]) => {
        // NB(milan): Maintaining continuity to only have the equals be supported for partials
        // i.e. omnibox
        if (scope == FieldScope.User && protoOperator != 'equals') {
          return null;
        }
        if (!c || c.length < partialExtractIdx + 1 || c[partialExtractIdx] == null) {
          return null;
        }
        const toRet = {
          kind,
          subkind: protoOperator,
          field: {
            type: CustomFieldType.Name,
            name: cleanString(c[partialExtractIdx].Field, scope, suffix),
          },
          value: [null],
        };

        if (partiallyEqualCriteria(result[protoOperator].criteria(toRet), [c[partialExtractIdx]])) {
          return toRet;
        }

        return null;
      },
      searchTypeToFilter: st => helpers.searchTypeToFilterWithField(st, searchTypeToFilterModifier),
      filterToSearchType: sf => helpers.filterToSearchTypeWithField(sf, filterToSearchTypeModifier),
    };
  }

  for (const k of ['equals', 'notEquals']) {
    result[k].withoutPartials = s => helpers.stripPartials(s, helpers.multiNotNullPartial);
    result[k] = withValueWrapping(result[k], 0);
  }

  for (const k of ['isSet', 'isUnset']) {
    result[k] = {
      withoutPartials: s => helpers.stripPartials(s, helpers.identity),
      searchTypeToFilter: st => helpers.searchTypeToFilterWithFieldIsSet(st),
      filterToSearchType: sf => helpers.filterToSearchTypeWithFieldIsSet(sf),
    };
  }

  for (const k of ['range', 'notRange']) {
    switch (suffix) {
      case VarType.Int:
      // fallthrough
      case VarType.Ints:
        result[k] = {
          withoutPartials: s => helpers.stripPartials(s, helpers.multiNotNull),
          searchTypeToFilter: helpers.searchTypeToFilterNamedNumericRangeValue,
          filterToSearchType: helpers.filterToSearchTypeWithFieldNumericRangeValue,
        };
        break;
      case VarType.Real:
      // fallthrough
      case VarType.Reals:
        result[k] = {
          withoutPartials: s => helpers.stripPartials(s, helpers.multiNotNull),
          searchTypeToFilter: helpers.searchTypeToFilterNamedFloatRangeValue,
          filterToSearchType: helpers.filterToSearchTypeNamedFloatRangeValue,
        };
        break;
      default:
        assertExhaustive(suffix);
    }
  }

  return result;
}

/**
 * Date helpers represent the nuances between the different proto/search operators
 * and how they should be parsed and interpreted. See `getCustomDateHelpers`.
 */
type dateHelpers = {
  withoutPartials: (value: any) => any;
  fromFn: (value: any) => any;
  filterToSt: (value: any) => any;
  stToFilter: (value: any) => any;
  extractFn: (value: any) => any;
};

/**
 * For a given proto operator, return the date parsing/extracting functionality
 * required in that context.
 * @param protoOperator
 */
function getCustomDateHelpers(protoOperator: string): dateHelpers {
  const toRet: dateHelpers = {
    withoutPartials: helpers.notNullPartial,
    fromFn: helpers.fromDate,
    filterToSt: helpers.filterToSearchTypeNamedDateValue,
    stToFilter: helpers.searchTypeToFilterWithFieldDateValue,
    extractFn: helpers.toDate,
  };

  let not = false;
  switch (protoOperator) {
    // In the UI we treat `is` as a single date range, but we need to parse
    // to/from proto/search-criteria as closed-open range over a single day.
    case UiOnlySubkinds.isDateLike:
      toRet.fromFn = helpers.fromSingleDateAsRange;
      toRet.filterToSt = v => null; // UI only construct
      toRet.stToFilter = helpers.searchTypeToFilterWithFieldFromSingleDateAsRange;
      toRet.extractFn = v => {
        // In order for a date range to be considered as a single date when going from
        // data on wire to the UI, all members need to be non null, and they all have to
        // be valid string representations of dates.
        const newV = helpers.mapNonNull(v, helpers.toDate);
        if (newV !== null) {
          return helpers.toDate(v[0]);
        }
        return null;
      };
      break;
    // Nothing different needed for below two
    case 'after':
    case 'before':
      break;
    // @ts-ignore
    case 'notRange':
      // `notRange` and `range` share a lot of similarities but there's a little bit of nuance:
      // A (positive) `range` can be represented as `isDateLike` or a single date in the data model. This is
      // only true if the dates are separated by exactly a day. `notRange` doesn't work like that,
      // and indicates that we shouldn't even try to parse it as such.
      not = true;
    case 'range':
      toRet.withoutPartials = helpers.multiNotNull;
      toRet.fromFn = helpers.fromDateRange;
      toRet.filterToSt = sf => helpers.filterToSearchTypeNameDateRange(sf, not);
      toRet.stToFilter = helpers.searchTypeToFilterWithFieldDateRangeValue;
      toRet.extractFn = helpers.toDateRange;
      break;
    // @ts-ignore
    case UiOnlySubkinds.notLastDurationLike:
      // `notLast` and `last` share a lot of similarities. In this case
      // the not is used to determine which UiOnlySubkinds to return in
      // `searchTypeToFilterNamedDurationRange`
      not = true;
    case UiOnlySubkinds.lastDurationLike:
      toRet.withoutPartials = helpers.durationPartial;
      toRet.fromFn = helpers.fromDurationToSolr;
      toRet.filterToSt = v => null; // UI only construct
      toRet.stToFilter = v => helpers.searchTypeToFilterWithFieldDurationRange(v, not);
      toRet.extractFn = helpers.toDurationFromSolr;
      break;
    // @ts-ignore
    case UiOnlySubkinds.notNextDurationLike:
      not = true;
    case UiOnlySubkinds.nextDurationLike:
      toRet.withoutPartials = helpers.durationPartial;
      toRet.fromFn = v => null; // don't need the legacy converter here
      toRet.filterToSt = v => null; // UI only construct
      toRet.stToFilter = v => helpers.searchTypeToFilterWithFieldDurationRange(v, not, true);
      toRet.extractFn = helpers.toDurationFromSolr;
      break;
  }
  return toRet;
}

/**
 * Returns a Converter Spec Map for a particular specified scope. The returned map
 * reflects the operator map where for each proto based operator (which is also the search type subkind)
 * there exists a converter spec for that. These helper functions are shared across the custom fields
 * implementations, since the primary difference is in the scope values, e.g.:
 *
 *    >> evt_fieldname_int
 *    >> user_fieldname_int
 *    >> user_fieldname_reals
 *
 * and parsing them from search criteria is a matter of checking whether the first bits are
 * evt or user (or other scopes not yet implemented).
 *
 * Since the mapping is from proto operator -> search operator, and search types are the same as
 * their proto representations, a proto user filter can key directly into the map to get the
 * converter spec rather than iterating over it and checking to see if the produced criteria
 * matches. E.g given a proto segment definition:
 *
 *   userVarDate: {
 *     range: {
 *       name: "date test",
 *       value: {
 *         startTime: "NOW/DAY-10DAY"
 *         endTime: "NOW/DAY+1DAY"
 *       }
 *     }
 *   }
 *
 *  range is used to key into the map and fetch the appropriate converter spec.
 *
 * @param kind The top level search type kind
 * @param scope The var scope, e.g. evt or user or other
 * @param suffix oneof date or dates
 */
export function customVarDates<T extends SearchType, K extends Kind<T>>(
  kind: K,
  scope: FieldScope,
  suffix: VarType.Date | VarType.Dates,
): SubkindConverterMap<T, K> {
  const result = newConverterMap<T, K>();
  const operators: Map<string, Op> = new Map([
    // These are single day ranges, i.e. happened on a specific day.
    [UiOnlySubkinds.isDateLike, Op.SEARCH_OP_RNGCO],
    ['after', Op.SEARCH_OP_GT],
    ['before', Op.SEARCH_OP_LT],
    ['range', Op.SEARCH_OP_RNGCC],
    ['notRange', Op.SEARCH_OP_NOT_RNGCC],
    [UiOnlySubkinds.lastDurationLike, Op.SEARCH_OP_RNGCC],
    [UiOnlySubkinds.notLastDurationLike, Op.SEARCH_OP_NOT_RNGCC],
    [UiOnlySubkinds.nextDurationLike, Op.SEARCH_OP_RNGCC],
    [UiOnlySubkinds.notNextDurationLike, Op.SEARCH_OP_NOT_RNGCC],
  ]);

  const partialExtractIdx = scope == FieldScope.Event ? 2 : 0;
  for (const [protoOperator, searchOperator] of operators.entries()) {
    const dateHelpers = getCustomDateHelpers(protoOperator);
    result[protoOperator] = {
      criteria: s => {
        if (isCustomDateType(s)) {
          switch (s.field.type) {
            case CustomFieldType.Name:
              // only one we need to support for legacy criteria
              return [
                {
                  Field: `${scope}_${s.field.name}_${suffix}`,
                  Op: searchOperator,
                  Value: dateHelpers.fromFn(s.value),
                },
              ];
          }
        }
        return [];
      },
      withoutPartials: s => helpers.stripPartials(s, dateHelpers.withoutPartials),
      extractFromCriteria: (c: DocumentCriterion[]) => {
        if (c[0] != null) {
          const value = dateHelpers.extractFn(c[0].Value);
          if (value != null) {
            return {
              kind,
              subkind: protoOperator,
              field: {
                type: CustomFieldType.Name,
                name: cleanString(c[0].Field, scope, suffix),
              },
              value,
            };
          }
        }
        return null;
      },
      extractFromPartialCriteria: (c: DocumentCriterion[]) => {
        if (scope == FieldScope.User && protoOperator != UiOnlySubkinds.isDateLike) {
          return null;
        }
        if (!c || c.length < partialExtractIdx + 1 || c[partialExtractIdx] == null) {
          return null;
        }
        const toRet = {
          kind,
          subkind: protoOperator,
          field: {
            type: CustomFieldType.Name,
            name: cleanString(c[partialExtractIdx].Field, scope, suffix),
          },
          value: new Date(),
        };

        if (partiallyEqualCriteria(result[protoOperator].criteria(toRet), [c[partialExtractIdx]])) {
          toRet.value = null;
          return toRet;
        }

        return null;
      },
      searchTypeToFilter: dateHelpers.stToFilter,
      filterToSearchType: dateHelpers.filterToSt,
    };
  }

  result[UiOnlySubkinds.isDateLike] = withValueWrapping(result[UiOnlySubkinds.isDateLike], 0);

  for (const k of ['isSet', 'isUnset']) {
    result[k] = {
      withoutPartials: s => helpers.stripPartials(s, helpers.identity),
      searchTypeToFilter: st => helpers.searchTypeToFilterWithFieldIsSet(st),
      filterToSearchType: sf => helpers.filterToSearchTypeWithFieldIsSet(sf),
    };
  }

  return result;
}

/**
 * Returns a Converter Spec Map for a particular specified scope. The returned map
 * reflects the operator map where for each proto based operator (which is also the search type subkind)
 * there exists a converter spec for that. These helper functions are shared across the custom fields
 * implementations, since the primary difference is in the scope values, e.g.:
 *
 *    >> evt_fieldname_int
 *    >> user_fieldname_int
 *    >> user_fieldname_reals
 *
 * and parsing them from search criteria is a matter of checking whether the first bits are
 * evt or user (or other scopes not yet implemented).
 *
 * Since the mapping is from proto operator -> search operator, and search types are the same as
 * their proto representations, a proto user filter can key directly into the map to get the
 * converter spec rather than iterating over it and checking to see if the produced criteria
 * matches. E.g given a proto segment definition:
 *
 *   userVarBool: {
 *     equals: {
 *       name: "bool test",
 *       value: true
 *     }
 *   }
 *
 *  equals is used to key into the map and fetch the appropriate converter spec.
 *
 * @param kind The top level search type kind
 * @param scope The var scope, e.g. evt or user or other
 * @param suffix oneof bool or bools
 */
export function customVarBools<T extends SearchType, K extends Kind<T>>(
  kind: K,
  scope: FieldScope,
  suffix: VarType.Bool | VarType.Bools,
): SubkindConverterMap<T, K> {
  const result = newConverterMap<T, K>();
  const operators: Map<string, Op> = new Map([
    // These are single day ranges, i.e. happened on a specific day.
    [segment.CustomFieldBoolOperator.operator_keys.equals, Op.SEARCH_OP_EQ],
    [segment.CustomFieldBoolOperator.operator_keys.notEquals, Op.SEARCH_OP_NEQ],
  ]);

  const partialExtractIdx = scope == FieldScope.Event ? 2 : 0;
  for (const [protoOperator, searchOperator] of operators.entries()) {
    result[protoOperator] = {
      criteria: s => {
        if (isCustomBoolType(s)) {
          switch (s.field.type) {
            case CustomFieldType.Name:
              // only one we need to support for legacy criteria
              return [
                { Field: `${scope}_${s.field.name}_${suffix}`, Op: searchOperator, Value: s.value },
              ];
          }
        }
        return [];
      },
      withoutPartials: s => helpers.stripPartials(s, helpers.identity),
      extractFromCriteria: (c: DocumentCriterion[]) => {
        if (c[0] != null) {
          return {
            kind,
            subkind: protoOperator,
            field: {
              type: CustomFieldType.Name,
              name: cleanString(c[0].Field, scope, suffix),
            },
            value: c[0].Value as boolean,
          };
        }
        return null;
      },
      extractFromPartialCriteria: (c: DocumentCriterion[]) => {
        if (!c || c.length < partialExtractIdx + 1 || c[partialExtractIdx] == null) {
          return null;
        }
        const toRet = {
          kind,
          subkind: protoOperator,
          field: {
            type: CustomFieldType.Name,
            name: cleanString(c[partialExtractIdx].Field, scope, suffix),
          },
          value: null,
        };

        if (partiallyEqualCriteria(result[protoOperator].criteria(toRet), [c[partialExtractIdx]])) {
          return toRet;
        }

        return null;
      },
      searchTypeToFilter: st => helpers.searchTypeToFilterWithField(st),
      filterToSearchType: sf => helpers.filterToSearchTypeWithField(sf),
    };
  }

  for (const k of ['isSet', 'isUnset']) {
    result[k] = {
      withoutPartials: s => helpers.stripPartials(s, helpers.identity),
      searchTypeToFilter: st => helpers.searchTypeToFilterWithFieldIsSet(st),
      filterToSearchType: sf => helpers.filterToSearchTypeWithFieldIsSet(sf),
    };
  }

  return result;
}
